import {
  keepPreviousData,
  useMutation,
  useQueries,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import type { QueryClient } from "@tanstack/react-query";

import { idempotencyKey, isApiError } from "@magicweave/api";
import type {
  AcceptedInvitation,
  Activation,
  AdNetworkStatus,
  AdNetworkTest,
  AdRevenue,
  BillingRecord,
  CreditPackCheckout,
  CreditPackQuote,
  BillingTaxProfile,
  Change,
  Entitlement,
  Invoice,
  Plan,
  PlanChange,
  PlanUsage,
  PlanUsageHistory,
  PlayerUsage,
  UsageGuardrails,
  RateCard,
  SetupStatus,
  TaxProfileSpec,
  Credential,
  Currency,
  CurrencyMovementReport,
  EconomyHealthReport,
  Environment,
  EnvironmentRedemption,
  EnvironmentPlayerActivity,
  EventActivity,
  InsightsCheck,
  InsightsDaily,
  InsightsSource,
  IntegrationAuditEntry,
  IntegrationEntry,
  Install,
  Invitation,
  IssuedCredential,
  LeaderboardDefinition,
  LeaderboardStandings,
  LedgerEntry,
  Member,
  MemberSeats,
  CoinStanding,
  NetworkAcceptance,
  NetworkAcceptanceSetting,
  NetworkMembership,
  NetworkShelfListing,
  NetworkShopCard,
  Org,
  OverageSwitch,
  PlayerDetail,
  PlayerStanding,
  PlayerStreak,
  Project,
  RedemptionState,
  Release,
  Role,
  RuleActivity,
  SignInProviderEntry,
  StatDefinition,
  TraceDetail,
  TraceSummary,
  TrialResult,
  UsageEvent,
  Viewer,
  // Not a domain type on its own — see the block at the end of this file that
  // lifts PlayerDocument/PlayerSave out of it. Packet-scoped: packages/api's
  // own re-export list (index.ts) is not owned by this packet, and the raw
  // schema is already exported under this name.
  ControlComponents,
} from "@magicweave/api";

import { useApi } from "./context";

/**
 * Every server read and write the console performs, in one file.
 *
 * Query keys are hierarchical and mirror the URL structure, so invalidating
 * `["environment", envId]` drops everything scoped to that environment in one
 * call. Getting this wrong is how a screen shows a stale balance after a grant
 * that visibly succeeded — so the invalidations below name what actually
 * changed rather than clearing the whole cache, which would work but would make
 * every write refetch the entire console.
 */

export const keys = {
  viewer: ["viewer"] as const,
  projects: (orgId: string) => ["org", orgId, "projects"] as const,
  project: (projectId: string) => ["project", projectId] as const,
  environments: (projectId: string) => ["project", projectId, "environments"] as const,
  releases: (projectId: string) => ["project", projectId, "releases"] as const,
  setupStatus: (projectId: string) => ["project", projectId, "setup-status"] as const,
  environment: (envId: string) => ["environment", envId] as const,
  credentials: (envId: string) => ["environment", envId, "credentials"] as const,
  installs: (envId: string) => ["environment", envId, "installs"] as const,
  members: (orgId: string) => ["org", orgId, "members"] as const,
  entitlement: (orgId: string) => ["org", orgId, "entitlement"] as const,
  membership: (orgId: string) => ["org", orgId, "network-membership"] as const,
  networkShelf: (orgId: string) => ["org", orgId, "network-shelf"] as const,
  networkAcceptance: (orgId: string) => ["org", orgId, "network-acceptance"] as const,
  networkAcceptanceSetting: (orgId: string) =>
    ["org", orgId, "network-acceptance-setting"] as const,
  coinStanding: (orgId: string) => ["org", orgId, "coin-standing"] as const,
  planUsage: (orgId: string) => ["org", orgId, "usage"] as const,
  playerUsage: (orgId: string) => ["org", orgId, "players", "usage"] as const,
  usageGuardrails: (orgId: string) => ["org", orgId, "usage", "guardrails"] as const,
  planUsageHistory: (orgId: string) => ["org", orgId, "usage", "history"] as const,
  plans: ["plans"] as const,
  billing: (orgId: string) => ["org", orgId, "billing"] as const,
  invoice: (orgId: string, invoiceId: string) => ["org", orgId, "invoice", invoiceId] as const,
  taxProfile: (orgId: string) => ["org", orgId, "tax-profile"] as const,
  creditPacks: (orgId: string) => ["org", orgId, "credit-packs"] as const,
  rateCard: ["rate-card"] as const,
  invitations: (orgId: string) => ["org", orgId, "invitations"] as const,
  roles: ["roles"] as const,
  players: (envId: string, cursor: string) =>
    ["environment", envId, "players", cursor] as const,
  player: (envId: string, playerId: string) =>
    ["environment", envId, "player", playerId] as const,
  currencies: (envId: string) => ["environment", envId, "currencies"] as const,
  stats: (envId: string) => ["environment", envId, "stats"] as const,
  leaderboards: (envId: string) => ["environment", envId, "leaderboards"] as const,
  standings: (envId: string, leaderboard: string, period: string, around: string) =>
    ["environment", envId, "leaderboards", leaderboard, period, around] as const,
  playerStandings: (envId: string, playerId: string) =>
    ["environment", envId, "player", playerId, "standings"] as const,
  playerStreaks: (envId: string, playerId: string) =>
    ["environment", envId, "player", playerId, "streaks"] as const,
  activations: (envId: string) => ["environment", envId, "activations"] as const,
  trace: (envId: string, traceId: string) => ["environment", envId, "trace", traceId] as const,
  signInProviders: (envId: string) => ["environment", envId, "sign-in-providers"] as const,
  integrations: (envId: string) => ["environment", envId, "integrations"] as const,
  integrationAudit: (envId: string) => ["environment", envId, "integrations", "audit"] as const,
  adNetworks: (envId: string) => ["environment", envId, "ad-networks"] as const,
  adRevenue: (envId: string) => ["environment", envId, "ad-revenue"] as const,
  redemptions: (envId: string, state: string) =>
    ["environment", envId, "redemptions", state] as const,
  // ---- packet G: player documents + saves ---------------------------------
  playerDocumentCollections: (envId: string, playerId: string) =>
    ["environment", envId, "player", playerId, "documents"] as const,
  playerDocuments: (envId: string, playerId: string, collection: string, after: string) =>
    ["environment", envId, "player", playerId, "documents", collection, after] as const,
  playerSaves: (envId: string, playerId: string) =>
    ["environment", envId, "player", playerId, "saves"] as const,
  playerSave: (envId: string, playerId: string, slot: string) =>
    ["environment", envId, "player", playerId, "saves", slot] as const,
  // ---- packet H: rooms ------------------------------------------------------
  rooms: (envId: string) => ["environment", envId, "rooms"] as const,
  roomState: (envId: string, room: string) =>
    ["environment", envId, "rooms", room, "state"] as const,
};

/**
 * openapi-fetch returns `{ data, error }` and our middleware throws on failure,
 * so `error` is always undefined by the time we see the result. This unwraps
 * `data` and turns the impossible case into a real error rather than letting
 * `undefined` propagate into a component as a blank screen.
 */
export function unwrap<T>(result: { data?: T }): T {
  if (result.data === undefined) {
    throw new Error("the API returned no body for a successful response");
  }
  return result.data;
}

// ------------------------------------------------------------------- identity

export function useViewer() {
  const api = useApi();
  return useQuery({
    queryKey: keys.viewer,
    queryFn: async (): Promise<Viewer> => unwrap(await api.GET("/v1/me")),
    // Who you are and which orgs you belong to changes on the order of never.
    staleTime: 5 * 60_000,
  });
}

export function useCreateOrganisation() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: { name: string; slug: string; timezone?: string }): Promise<Org> =>
      unwrap(await api.POST("/v1/orgs", { body })),
    // The BFF rotates its server-held token before returning this write because
    // studio ownership changes the caller's scopes. Invalidating here reveals
    // Step 2 only after that cookie-backed session is ready to authorize it.
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.viewer }),
  });
}

/**
 * Rename a studio.
 *
 * The name only. The slug is in URLs, in CLI commands and in whatever somebody
 * has written down, so the server refuses to move it and this does not offer
 * to — see the endpoint's own description.
 */
export function useRenameOrganisation(orgId: string) {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: { name: string }): Promise<Org> =>
      unwrap(await api.PATCH("/v1/orgs/{orgId}", { params: { path: { orgId } }, body })),
    // The viewer carries every org's name, and the name is on the header of
    // every screen. Anything short of a refetch leaves the old one in the
    // corner of the page that was just used to change it.
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.viewer }),
  });
}

// ------------------------------------------------------------------- projects

export function useProjects(orgId: string | undefined, options: { pollMs?: number } = {}) {
  const api = useApi();
  const { pollMs } = options;
  return useQuery({
    queryKey: keys.projects(orgId ?? ""),
    enabled: Boolean(orgId),
    queryFn: async (): Promise<Project[]> => {
      const res = await api.GET("/v1/orgs/{orgId}/projects", {
        params: { path: { orgId: orgId as string } },
      });
      return unwrap(res).projects;
    },
    // Only while a screen is waiting for a game to appear — Claude is making
    // one somewhere else — and only while the tab is in front.
    refetchInterval: pollMs
      ? () => (document.visibilityState === "visible" ? pollMs : false)
      : false,
  });
}

export function useProject(projectId: string) {
  const api = useApi();
  return useQuery({
    queryKey: keys.project(projectId),
    queryFn: async (): Promise<Project> =>
      unwrap(await api.GET("/v1/projects/{projectId}", { params: { path: { projectId } } })),
    enabled: Boolean(projectId),
  });
}

/**
 * The shortest usable game setup: a project, a Development environment that
 * follows the draft, and an empty draft.
 *
 * It never asks for a client key. The key and its secret are made at "Add to
 * your game", when a build exists to hold them, so the response has none to
 * show and nothing here can leak one.
 */
export function useCreateStarterProject(orgId: string) {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: { name: string; slug: string }) =>
      unwrap(
        await api.POST("/v1/orgs/{orgId}/project-setup", {
          params: { path: { orgId } },
          body: { ...body, issue_credential: false },
        }),
      ),
    onSuccess: ({ project_id }) => {
      void qc.invalidateQueries({ queryKey: keys.projects(orgId) });
      void qc.invalidateQueries({ queryKey: keys.environments(project_id) });
      void qc.invalidateQueries({ queryKey: keys.setupStatus(project_id) });
    },
  });
}

/**
 * Where a project's first loop stands, read from control on every call.
 *
 * Nothing about it is stored, so there is nothing to keep in step: every draft
 * write invalidates `["project", id]`, which is this key's prefix, and a key
 * being issued or rotated invalidates it by name below. It also refetches when
 * the tab regains focus, and every ten seconds while the game is waiting on
 * something that happens outside the console — a build connecting, a first
 * payout — and only while the tab is in front.
 */
export function useSetupStatus(projectId: string) {
  const api = useApi();
  return useQuery({
    queryKey: keys.setupStatus(projectId),
    enabled: Boolean(projectId),
    queryFn: () => readSetupStatus(api, projectId),
    staleTime: 5_000,
    refetchOnWindowFocus: true,
    refetchInterval: (query) =>
      typeof document !== "undefined" && document.visibilityState !== "visible"
        ? false
        : setupPollMs(query.state.data),
  });
}

/**
 * How often to ask again: only while the step to do waits on something outside
 * the console. A project with no Development environment has nothing that can
 * connect, so it is not asked until the console makes one.
 */
export function setupPollMs(status: SetupStatus | undefined): number | false {
  if (!status || status.finished) return false;
  const waiting = status.steps.find((step) => !step.done);
  if (!waiting || waiting.blocked?.reason === "no_development_environment") return false;
  return waiting.id === "add_to_game" || waiting.id === "first_payout" ? 10_000 : false;
}

/** One project's setup status. Exported for `setup-statuses.ts`, which reads many at once. */
export async function readSetupStatus(
  api: ReturnType<typeof useApi>,
  projectId: string,
): Promise<SetupStatus> {
  return unwrap(
    await api.GET("/v1/projects/{projectId}/setup-status", {
      params: { path: { projectId } },
    }),
  );
}

/**
 * Every cached setup status, whichever project it belongs to.
 *
 * A credential is addressed by environment, and an environment does not carry
 * its project in the key, so the credential hooks cannot name the status they
 * change. Matching on the key's shape is the honest alternative to threading a
 * project id through every caller.
 */
function invalidateSetupStatus(qc: QueryClient) {
  void qc.invalidateQueries({
    predicate: (query) =>
      query.queryKey[0] === "project" && query.queryKey[2] === "setup-status",
  });
}

// --------------------------------------------------------------- environments

/** One project's environments. Exported so the games page reads them without a hook per game. */
export async function readEnvironments(
  api: ReturnType<typeof useApi>,
  projectId: string,
): Promise<Environment[]> {
  const res = await api.GET("/v1/projects/{projectId}/environments", {
    params: { path: { projectId } },
  });
  return unwrap(res).environments;
}

export function useEnvironments(projectId: string) {
  const api = useApi();
  return useQuery({
    queryKey: keys.environments(projectId),
    queryFn: () => readEnvironments(api, projectId),
    // The shell asks for these on every screen, including the ones outside a
    // project. Without this the query runs with an empty id and the server
    // answers 400 — harmless to the page, and a real error in the console for
    // anyone reading it while debugging something else.
    enabled: Boolean(projectId),
  });
}

export function useEnvironment(envId: string) {
  const api = useApi();
  return useQuery({
    queryKey: keys.environment(envId),
    queryFn: async (): Promise<Environment> =>
      unwrap(await api.GET("/v1/environments/{envId}", { params: { path: { envId } } })),
  });
}

/** Replace an environment's web addresses. Sent whole. */
export function useSetAllowedOrigins(projectId: string, envId: string) {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (origins: string[]) =>
      unwrap(
        await api.PUT("/v1/environments/{envId}/allowed-origins", {
          params: { path: { envId } },
          body: { origins },
        }),
      ),
    onSuccess: (environment) => {
      qc.setQueryData(keys.environment(envId), environment);
      return qc.invalidateQueries({ queryKey: keys.environments(projectId) });
    },
  });
}

export function useCreateEnvironment(projectId: string) {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: {
      name: string;
      slug: string;
      kind: "development" | "production";
      resolution_mode?: "pinned_release" | "latest_draft";
    }): Promise<Environment> =>
      unwrap(
        await api.POST("/v1/projects/{projectId}/environments", {
          params: { path: { projectId } },
          body,
        }),
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: keys.environments(projectId) });
      // The project card counts environments.
      void qc.invalidateQueries({ queryKey: keys.project(projectId) });
    },
  });
}

// ---------------------------------------------------------------- credentials

export function useCredentials(envId: string) {
  const api = useApi();
  return useQuery({
    queryKey: keys.credentials(envId),
    queryFn: async (): Promise<Credential[]> => {
      const res = await api.GET("/v1/environments/{envId}/credentials", {
        params: { path: { envId } },
      });
      return unwrap(res).credentials;
    },
  });
}

export function useIssueCredential(envId: string) {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: {
      kind: "client" | "server" | "preview";
      name?: string;
    }): Promise<IssuedCredential> =>
      unwrap(
        await api.POST("/v1/environments/{envId}/credentials", {
          params: { path: { envId } },
          body,
        }),
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: keys.credentials(envId) });
      void qc.invalidateQueries({ queryKey: keys.environment(envId) });
      invalidateSetupStatus(qc);
    },
  });
}

/**
 * Turning off a credential that has leaked.
 *
 * The row stays in the list with `revoked_at` set rather than disappearing,
 * because "this key was turned off on Tuesday" is the answer a studio's own
 * security review asks for, and a vanished row answers nothing. Revoking twice
 * succeeds on the server, so a double click is not an error the screen has to
 * explain.
 */
export function useRevokeCredential(envId: string) {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    // No unwrap: a 204 has no body. A refusal never reaches here — the client
    // middleware throws an ApiError on any non-ok response — so awaiting is
    // the whole check.
    mutationFn: async (credentialId: string): Promise<void> => {
      await api.DELETE("/v1/environments/{envId}/credentials/{credentialId}", {
        params: { path: { envId, credentialId } },
      });
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: keys.credentials(envId) });
      void qc.invalidateQueries({ queryKey: keys.environment(envId) });
    },
  });
}

/**
 * Replacing the secret on a credential that is still in service.
 *
 * Returns the new secret, which exists in this one response and nowhere else —
 * the caller must show it before anything can navigate away. The client id does
 * not change, so a build's configuration keeps working and only the secret has
 * to be redeployed.
 *
 * Deliberately not retried. A retry would mint a second secret and supersede
 * the one the developer is looking at, which is the one failure mode worse than
 * the request having failed outright.
 */
export function useRotateCredential(envId: string) {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    retry: false,
    mutationFn: async (credentialId: string): Promise<IssuedCredential> =>
      unwrap(
        await api.POST("/v1/environments/{envId}/credentials/{credentialId}/rotate", {
          params: { path: { envId, credentialId } },
        }),
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: keys.credentials(envId) });
      invalidateSetupStatus(qc);
    },
  });
}

/** The game installs enrolled against this environment, newest first. */
export function useInstalls(envId: string) {
  const api = useApi();
  return useQuery({
    queryKey: keys.installs(envId),
    queryFn: async (): Promise<Install[]> => {
      const res = await api.GET("/v1/environments/{envId}/installs", {
        params: { path: { envId } },
      });
      return unwrap(res).installs;
    },
  });
}

/**
 * Cutting off one install.
 *
 * The reason is required by the server rather than optional, and this is the
 * one place the console does not soften that: an install is revoked when
 * somebody has decided it is abusing the API, and the note is what the next
 * person reads when they ask why a player cannot get in.
 */
export function useRevokeInstall(envId: string) {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      installId,
      reason,
    }: {
      installId: string;
      reason: string;
    }): Promise<void> => {
      await api.PUT("/v1/environments/{envId}/installs/{installId}/revoked", {
        params: { path: { envId, installId } },
        body: { reason },
      });
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: keys.installs(envId) });
    },
  });
}

// -------------------------------------------------------------------- players

export function usePlayers(envId: string, cursor = "") {
  const api = useApi();
  return useQuery({
    queryKey: keys.players(envId, cursor),
    queryFn: async () => {
      const res = await api.GET("/v1/environments/{envId}/players", {
        params: { path: { envId }, query: cursor ? { after: cursor } : {} },
      });
      const page = unwrap(res);
      return { players: page.players, nextCursor: page.next_cursor };
    },
  });
}

/**
 * One player, with their balances, stats, holdings and recent movements from
 * the same instant.
 *
 * `enabled` on a truthy id, which the inspector never needs — it always has
 * one — and the players list depends on: it asks for a player by id only once
 * what has been typed into the filter is a whole one, so a partial id costs
 * nothing. The key is the inspector's own, so a lookup that finds somebody
 * warms the screen the reader is about to open.
 */
export function usePlayer(envId: string, playerId: string) {
  const api = useApi();
  return useQuery({
    queryKey: keys.player(envId, playerId),
    enabled: Boolean(playerId),
    queryFn: async (): Promise<PlayerDetail> =>
      unwrap(
        await api.GET("/v1/environments/{envId}/players/{playerId}", {
          params: { path: { envId, playerId } },
        }),
      ),
  });
}

// -------------------------------------------------------------------- economy

export function useCurrencies(envId: string) {
  const api = useApi();
  return useQuery({
    queryKey: keys.currencies(envId),
    queryFn: async (): Promise<Currency[]> => {
      const res = await api.GET("/v1/environments/{envId}/currencies", {
        params: { path: { envId } },
      });
      return unwrap(res).currencies;
    },
    // Guarded like every other environment-scoped read here. Without it, a
    // project with no environments sent `GET /v1/environments//currencies` —
    // a request with an empty path segment, which is not a question the API
    // can answer and not one worth asking. `routes/loops.tsx` reached it on a
    // brand-new project, which is the most common way to arrive.
    enabled: Boolean(envId),
  });
}

export interface AdjustInput {
  playerId: string;
  currencyKey: string;
  amount: number;
  direction: "grant" | "debit";
  note?: string;
}

export function useAdjustBalance(envId: string) {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: AdjustInput): Promise<LedgerEntry> =>
      unwrap(
        await api.POST("/v1/environments/{envId}/players/{playerId}/adjust", {
          params: {
            path: { envId, playerId: input.playerId },
            // One key per logical action. Reusing it across retries of the same
            // adjustment is what makes the retry safe; generating a new one per
            // attempt would defeat the mechanism entirely.
            header: { "Idempotency-Key": idempotencyKey() },
          },
          body: {
            currency_key: input.currencyKey,
            amount: input.amount,
            direction: input.direction,
            note: input.note,
          },
        }),
      ),
    onSuccess: (_entry, input) => {
      // Keep the success state pending until the inspector reflects the write.
      // Otherwise the dialog can close over a stale balance and ledger.
      return qc.invalidateQueries({ queryKey: keys.player(envId, input.playerId) });
    },
  });
}

// ---------------------------------------------------------------- progression

/**
 * What an environment is serving, as opposed to what the draft says.
 *
 * The same distinction the currency list draws, and it is the one thing a
 * definition screen cannot fold away: a board written this morning is in the
 * draft and is not live anywhere until a release carrying it is activated, and
 * two environments of one project can legitimately differ.
 */
export function useLiveStats(envId: string | undefined) {
  const api = useApi();
  return useQuery({
    queryKey: keys.stats(envId ?? ""),
    enabled: Boolean(envId),
    queryFn: async (): Promise<StatDefinition[]> => {
      const res = await api.GET("/v1/environments/{envId}/stats", {
        params: { path: { envId: envId as string } },
      });
      return unwrap(res).stats;
    },
  });
}

export function useLiveLeaderboards(envId: string | undefined) {
  const api = useApi();
  return useQuery({
    queryKey: keys.leaderboards(envId ?? ""),
    enabled: Boolean(envId),
    queryFn: async (): Promise<LeaderboardDefinition[]> => {
      const res = await api.GET("/v1/environments/{envId}/leaderboards", {
        params: { path: { envId: envId as string } },
      });
      return unwrap(res).leaderboards;
    },
  });
}

/**
 * A board's standings, together with the cycle they are in.
 *
 * One request, because they are one screen: a rank under the wrong heading is
 * worse than a rank that arrives a moment later. `period` empty reads whichever
 * cycle is open now, which is what opening the screen means.
 *
 * `around` centres the board on one player instead of reading its head. It is
 * the same request rather than a second one for the same reason the cycles ride
 * along: the picker, the size and the heading all still apply, and a
 * neighbourhood rendered under a stale heading is the bug this endpoint was
 * shaped to prevent.
 */
export function useStandings(
  envId: string | undefined,
  leaderboard: string,
  period = "",
  around = "",
) {
  const api = useApi();
  return useQuery({
    queryKey: keys.standings(envId ?? "", leaderboard, period, around),
    enabled: Boolean(envId && leaderboard),
    queryFn: async (): Promise<LeaderboardStandings> =>
      unwrap(
        await api.GET("/v1/environments/{envId}/leaderboards/{leaderboard}", {
          params: {
            path: { envId: envId as string, leaderboard },
            query: {
              ...(period ? { period } : {}),
              ...(around ? { around } : {}),
            },
          },
        }),
      ),
  });
}

/**
 * Where one player sits on every board, including the ones they are not on.
 *
 * Separate from the player read rather than folded into it: this is one rank
 * count per board a release declares, where the balances and the ledger beside
 * it are one query each. Loaded when the player screen renders, and never
 * polled — a rank does not move while somebody reads it.
 */
export function usePlayerStandings(envId: string, playerId: string) {
  const api = useApi();
  return useQuery({
    queryKey: keys.playerStandings(envId, playerId),
    queryFn: async (): Promise<PlayerStanding[]> => {
      const res = await api.GET("/v1/environments/{envId}/players/{playerId}/standings", {
        params: { path: { envId, playerId } },
      });
      return unwrap(res).standings;
    },
  });
}

/**
 * Where one player stands on every streak, including the ones they never
 * started.
 *
 * Separate from the player read for the reason the standings are: it is one
 * lookup per counting rule a release declares, where the balances and the
 * ledger beside it are one query each.
 *
 * Not polled, and this is the one place that deserves a sentence rather than a
 * convention. Whether a run is still alive is worked out by the server when the
 * question is asked, from the last period counted and the rule's grace as they
 * stand at that moment — there is no stored status and nothing sweeps one. So a
 * refetch tells the truth about the second it happens, and a page left open
 * overnight is stale in the ordinary way rather than showing a streak that a
 * background job has not yet been round to break.
 */
export function usePlayerStreaks(envId: string, playerId: string) {
  const api = useApi();
  return useQuery({
    queryKey: keys.playerStreaks(envId, playerId),
    queryFn: async (): Promise<PlayerStreak[]> => {
      const res = await api.GET("/v1/environments/{envId}/players/{playerId}/streaks", {
        params: { path: { envId, playerId } },
      });
      return unwrap(res).streaks;
    },
  });
}

// ------------------------------------------------------------------- releases

/** One project's releases. Exported so the games page reads them without a hook per game. */
export async function readReleases(
  api: ReturnType<typeof useApi>,
  projectId: string,
): Promise<Release[]> {
  const res = await api.GET("/v1/projects/{projectId}/releases", {
    params: { path: { projectId } },
  });
  return unwrap(res).releases;
}

export function useReleases(projectId: string) {
  const api = useApi();
  return useQuery({
    queryKey: keys.releases(projectId),
    queryFn: () => readReleases(api, projectId),
  });
}

export function usePublishRelease(projectId: string) {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: {
      draft_name?: string;
      version?: string;
      notes?: string;
    }): Promise<Release> =>
      unwrap(
        await api.POST("/v1/projects/{projectId}/releases", {
          params: {
            path: { projectId },
            header: { "Idempotency-Key": idempotencyKey() },
          },
          body,
        }),
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: keys.releases(projectId) });
      void qc.invalidateQueries({ queryKey: keys.project(projectId) });
    },
  });
}

export function useReleaseDiff(projectId: string, from: string | null, to: string | null) {
  const api = useApi();
  return useQuery({
    queryKey: ["project", projectId, "diff", from, to] as const,
    enabled: Boolean(from && to),
    queryFn: async (): Promise<Change[]> => {
      const res = await api.GET("/v1/projects/{projectId}/releases/diff", {
        params: { path: { projectId }, query: { from: from as string, to: to as string } },
      });
      return unwrap(res).changes;
    },
    // A diff between two immutable releases cannot change. Refetching it is
    // pure waste, and it is the request a release screen makes most often.
    staleTime: Infinity,
  });
}

export function useActivations(envId: string) {
  const api = useApi();
  return useQuery({
    queryKey: keys.activations(envId),
    queryFn: async (): Promise<Activation[]> => {
      const res = await api.GET("/v1/environments/{envId}/activations", {
        params: { path: { envId } },
      });
      return unwrap(res).activations;
    },
  });
}

export interface MoveReleaseInput {
  action: "activated" | "rolled_back" | "promoted";
  release_id?: string;
  from_environment_id?: string;
  note?: string;
}

export function useMoveRelease(envId: string, projectId: string) {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: MoveReleaseInput): Promise<Environment> =>
      unwrap(
        await api.PUT("/v1/environments/{envId}/release", {
          params: {
            path: { envId },
            header: { "Idempotency-Key": idempotencyKey() },
          },
          body,
        }),
      ),
    onSuccess: () => {
      // What is live changed, so: this environment, the project's environment
      // list, the release list (each release reports where it is live), and the
      // audit timeline. Also the currencies, which resolve through the pointer
      // that just moved — the one most easily forgotten, and the one whose
      // staleness would be least obvious on screen.
      void qc.invalidateQueries({ queryKey: keys.environment(envId) });
      void qc.invalidateQueries({ queryKey: keys.environments(projectId) });
      void qc.invalidateQueries({ queryKey: keys.releases(projectId) });
      void qc.invalidateQueries({ queryKey: keys.activations(envId) });
      void qc.invalidateQueries({ queryKey: keys.currencies(envId) });
    },
  });
}

// ------------------------------------------------------------ events and rules

/**
 * Live events, and one player's history.
 *
 * Both are polled rather than pushed. A screen someone leaves open while a
 * playtest runs has to keep up, and a websocket for two read-only screens is a
 * second transport to operate for a refresh interval that a person cannot tell
 * apart from live.
 */
/** What an event led to, as the Live events chips ask it. */
export type Caused = "paid" | "nothing" | "undeclared";

export function useEvents(
  envId: string,
  filter: { type?: string; playerId?: string; caused?: Caused } = {},
) {
  const api = useApi();
  return useQuery({
    queryKey: [
      ...keys.environment(envId),
      "events",
      filter.type ?? "",
      filter.playerId ?? "",
      filter.caused ?? "",
    ],
    queryFn: async () =>
      unwrap(
        await api.GET("/v1/environments/{envId}/events", {
          params: {
            path: { envId },
            query: {
              type: filter.type || undefined,
              playerId: filter.playerId || undefined,
              // Server-side on purpose: the page is chosen there, so a chip
              // applied in the client would return a short page rather than a
              // page of the right rows.
              caused: filter.caused || undefined,
              limit: 100,
            },
          },
        }),
      ).events,
    refetchInterval: 4000,
  });
}

export function usePlayerTimeline(envId: string, playerId: string) {
  const api = useApi();
  return useQuery({
    queryKey: [...keys.player(envId, playerId), "timeline"],
    queryFn: async () =>
      unwrap(
        await api.GET("/v1/environments/{envId}/players/{playerId}/timeline", {
          params: { path: { envId, playerId }, query: { limit: 100 } },
        }),
      ).entries,
    refetchInterval: 5000,
  });
}

/** The documents in the working draft — currencies, event types and rules. */
/**
 * What an event type has actually been carrying.
 *
 * One query per type rather than one for the whole environment: the screens that
 * need it need one at a time, and a bulk endpoint would be a second shape of the
 * same answer. `enabled` keeps it off the wire until something asks.
 */
export function useEventShape(envId: string, type: string | undefined) {
  const api = useApi();
  return useQuery({
    queryKey: ["environment", envId, "shape", type],
    enabled: Boolean(envId) && Boolean(type),
    queryFn: async () =>
      unwrap(
        await api.GET("/v1/environments/{envId}/events/{eventType}/shape", {
          params: { path: { envId, eventType: type ?? "" } },
        }),
      ),
    // Measured from a thirty-day window; it does not move minute to minute.
    staleTime: 5 * 60 * 1000,
  });
}

export function useDraftDocuments(projectId: string) {
  const api = useApi();
  return useQuery({
    queryKey: [...keys.project(projectId), "draft"],
    queryFn: async () =>
      unwrap(
        await api.GET("/v1/projects/{projectId}/draft", { params: { path: { projectId } } }),
      ).documents,
  });
}

/**
 * What a draft write invalidates, and why it is more than the project.
 *
 * A document lives under the project, so the obvious invalidation is the
 * project's queries — and that is all this did. But an environment in
 * `latest_draft` mode serves the draft directly, so writing a document changes
 * what that environment is *serving*, and the environment's own reads
 * (`/environments/{id}/stats`, `/leaderboards`, `/currencies`) are keyed under
 * `environment`, not under `project`. Nothing connected the two.
 *
 * The symptom was the worst kind: Stats saved a stat, said "not live yet"
 * beside it, and cleared the badge on reload — on a screen whose own copy says
 * a change is live in Development the moment you save it. The write had worked.
 * The screen was reading a cached answer from before it.
 *
 * Every environment rather than the ones that follow the draft, because the
 * client does not know which those are and the server is the only thing that
 * does. It is a handful of queries.
 */
function invalidateServedConfiguration(qc: QueryClient, projectId: string) {
  void qc.invalidateQueries({ queryKey: keys.project(projectId) });
  void qc.invalidateQueries({ queryKey: ["environment"] });
}

/**
 * Write one document into the working draft, optionally refusing a stale write.
 *
 * This is the one save. The working draft is shared mutable state — MCP writes
 * into it, the CLI writes into it, and so does every form in this console — and
 * a save is a whole-file PUT, so without a version the last writer wins in
 * silence. An agent adding a stage while somebody renames one here does not
 * collide; it erases.
 *
 * `version` is the content hash the document was read at, from
 * `useDraftDocuments` (every document it returns carries one). Three cases, and
 * no magic numbers:
 *
 *   - **absent** — unconditional, which is the old behaviour said out loud. The
 *     returned `guarded` is false so a screen can tell somebody their save was
 *     not protected rather than implying it was.
 *   - **a hash** — only if the document is still those bytes. A 409 otherwise,
 *     with nothing written; `stale` reports it.
 *   - **`""`** — only if nothing is at that path. That is for creating a path,
 *     and never for a document on screen. `versionOf` in `document/save.ts`
 *     refuses to hand it back for exactly that reason.
 *
 * The 200 body carries the **new** version — the hash of the bytes just
 * accepted — so a form saving twice in a row should take `data.version` for the
 * second save rather than re-reading the draft.
 *
 * There was briefly a second hook for this in `document/save.ts`, written when
 * the contract had no version at all. Two save hooks is how the hole comes back
 * on one screen, so there is one.
 */
export function usePutDraftDocument(projectId: string) {
  const api = useApi();
  const qc = useQueryClient();
  const mutation = useMutation({
    mutationFn: async (doc: { path: string; content: string; version?: string }) =>
      unwrap(
        await api.PUT("/v1/projects/{projectId}/draft/document", {
          params: { path: { projectId } },
          body: {
            path: doc.path,
            content: doc.content,
            // Omitted, not a sentinel: absent means unconditional, and there is
            // no value that means "guard me with nothing".
            ...(doc.version === undefined ? {} : { if_version: doc.version }),
          },
        }),
      ),
    onSuccess: () => invalidateServedConfiguration(qc, projectId),
  });

  return {
    ...mutation,
    /** True when the draft moved underneath this save and the server refused it. */
    stale: isApiError(mutation.error) && mutation.error.status === 409,
    /** False when no version was known, so nothing could be conditioned on. */
    guarded: mutation.variables?.version !== undefined,
  };
}

export function useDeleteDraftDocument(projectId: string) {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (path: string) => {
      await api.DELETE("/v1/projects/{projectId}/draft/document", {
        params: { path: { projectId }, query: { path } },
      });
    },
    onSuccess: () => invalidateServedConfiguration(qc, projectId),
  });
}

/**
 * What the working draft would change in one environment.
 *
 * Originally added for the asset library's one question nothing else could
 * answer — a live release pinning a file the draft no longer carries — this
 * hook itself is general: it reads `/v1/projects/{projectId}/draft/diff` and
 * says nothing about what changed, which is why it is also used by rules,
 * shops, inventory and the changes panel.
 */
export function useDraftDiff(projectId: string, envId: string | undefined) {
  const api = useApi();
  return useQuery({
    queryKey: [...keys.project(projectId), "draft-diff", envId ?? ""] as const,
    enabled: Boolean(envId),
    queryFn: async (): Promise<Change[]> =>
      unwrap(
        await api.GET("/v1/projects/{projectId}/draft/diff", {
          params: { path: { projectId }, query: { environment_id: envId as string } },
        }),
      ).changes,
  });
}

// ------------------------------------------------------------------- requests

/**
 * How far back a page of requests looks, and how many it asks for.
 *
 * The server caps a page at 200 and only offers one filter — "show me what
 * failed". Everything else the screen offers (slowest first, only what carried
 * a game event) is applied to the page it already has, which is honest for a
 * screen that says how many it loaded. Asking for the maximum makes those
 * client-side controls useful rather than misleading.
 */
export const REQUEST_PAGE = 200;

export interface TraceFilter {
  /** Server-side, because "what broke" must not be limited to one page. */
  failedOnly?: boolean;
  /** Page backwards from this moment. Empty means now. */
  before?: string;
  /**
   * Put the console's own reads back in. Server-side, because the page is
   * chosen by the server: filtering them away in the client produces a short
   * page rather than a page of the right rows.
   */
  includeConsole?: boolean;
}

/**
 * Try an event against the rules an environment is serving.
 *
 * A mutation rather than a query even though it changes nothing: it is asked
 * by pressing a button, not by opening a screen, and caching the answer to
 * "what would this payload do" would show a stale one after the rule is
 * edited — which is precisely the moment somebody asks again.
 */
export interface TrialReport {
  declared: boolean;
  results: TrialResult[];
}

export function useTryEvent(envId: string) {
  const api = useApi();
  return useMutation({
    mutationFn: async (input: {
      type: string;
      data: Record<string, unknown>;
    }): Promise<TrialReport> =>
      unwrap(
        await api.POST("/v1/environments/{envId}/rules/trial", {
          params: { path: { envId } },
          body: { type: input.type, data: input.data },
        }),
      ),
  });
}

export function useTraces(envId: string, filter: TraceFilter = {}) {
  const api = useApi();
  const before = filter.before ?? "";
  return useQuery({
    queryKey: [
      ...keys.environment(envId),
      "traces",
      filter.failedOnly ? "failed" : "all",
      filter.includeConsole ? "with-console" : "game-only",
      before,
    ],
    queryFn: async (): Promise<TraceSummary[]> =>
      unwrap(
        await api.GET("/v1/environments/{envId}/traces", {
          params: {
            path: { envId },
            query: {
              failedOnly: filter.failedOnly || undefined,
              includeConsole: filter.includeConsole || undefined,
              before: before || undefined,
              limit: REQUEST_PAGE,
            },
          },
        }),
      ).traces,
    // Polled while the reader is looking at the newest requests, and not at
    // all once they have paged backwards: an older page cannot gain rows, and
    // refetching it would only reset the scroll position of somebody reading.
    refetchInterval: before ? false : 5000,
  });
}

/**
 * One request in full.
 *
 * Not polled. A finished request does not change, and the one case where it
 * still might — steps that had not reached the store when the page loaded — is
 * something the screen names and offers a button for, rather than papering
 * over with a refresh interval that makes the waterfall move under the cursor.
 */
export function useTrace(envId: string, traceId: string) {
  const api = useApi();
  return useQuery({
    queryKey: keys.trace(envId, traceId),
    queryFn: async (): Promise<TraceDetail> =>
      unwrap(
        await api.GET("/v1/environments/{envId}/traces/{traceId}", {
          params: { path: { envId, traceId } },
        }),
      ),
  });
}

// ------------------------------------------------------------------- overview

/**
 * How far back Overview's activity panels look.
 *
 * Three choices, not a date picker. Decisions/0012 gives non-developers filter
 * chips and no query language, and a time range is the one filter every panel
 * on the screen shares — so it is chosen once and applied to all three.
 *
 * The bucket travels with the span rather than being a second control. What a
 * column of the arrivals chart covers is a consequence of how wide a window is
 * being read, not an independent decision anybody wants to make.
 */
export type ActivitySpan = "hour" | "day" | "week";

export const ACTIVITY_SPANS: Record<
  ActivitySpan,
  { ms: number; bucket: "minute" | "hour" | "day"; words: string; short: string }
> = {
  /*
   * Two spellings, because they do two jobs.
   *
   * `words` finishes a sentence — "Nothing has reached this rule in the last
   * 24 hours" — and every panel that names its period uses it. `short` labels
   * the control that picks it, where a sentence fragment repeated three times
   * is both hard to scan and wide enough to push the document sideways: the
   * three long forms measured 353px in a segmented group, which is more than
   * a 390px viewport has to give.
   */
  hour: { ms: 3_600_000, bucket: "minute", words: "the last hour", short: "1 hour" },
  day: { ms: 24 * 3_600_000, bucket: "hour", words: "the last 24 hours", short: "24 hours" },
  week: { ms: 7 * 24 * 3_600_000, bucket: "day", words: "the last 7 days", short: "7 days" },
};

/**
 * `since` is computed when the request is made rather than when the component
 * renders.
 *
 * Putting a timestamp in the query key would make every render a different key
 * and every render a fresh request, and the cache would never hit. Keying on
 * the span and resolving the moment inside the fetch keeps one entry per
 * choice, which is what refetching on an interval means at all.
 */
function since(span: ActivitySpan): string {
  return new Date(Date.now() - ACTIVITY_SPANS[span].ms).toISOString();
}

/**
 * These three are the counts Overview is built from, and they refresh slowly on
 * purpose.
 *
 * Live events polls every four seconds because somebody is watching a playtest
 * happen. Nobody watches a total over the last 24 hours change, and a summary
 * that reanimates while being read is harder to read — so this is thirty
 * seconds, and the panels say which period they cover rather than implying they
 * are live.
 */
const ACTIVITY_REFRESH = 30_000;

/**
 * Events over a span, or with `days` over that many inclusive UTC days ending
 * today, answered per day (`span` is then only the key's fallback).
 */
export function useEventActivity(envId: string, span: ActivitySpan, days?: number) {
  const api = useApi();
  return useQuery({
    queryKey: [...keys.environment(envId), "event-activity", days ?? span] as const,
    queryFn: async (): Promise<EventActivity> =>
      unwrap(
        await api.GET("/v1/environments/{envId}/events/activity", {
          params: {
            path: { envId },
            query:
              days === undefined
                ? { since: since(span), bucket: ACTIVITY_SPANS[span].bucket }
                : { since: daysSince(days), bucket: "day" },
          },
        }),
      ),
    refetchInterval: days === undefined ? ACTIVITY_REFRESH : 5 * 60_000,
  });
}

/** How far back Overview's Players group looks, in inclusive UTC days. */
export type PlayerRange = "week" | "month" | "quarter";

export const PLAYER_RANGES: Record<
  PlayerRange,
  { days: number; short: string; words: string }
> = {
  week: { days: 7, short: "7 days", words: "the last 7 days" },
  month: { days: 30, short: "30 days", words: "the last 30 days" },
  quarter: { days: 90, short: "90 days", words: "the last 90 days" },
};

/** UTC midnight of the first of `days` inclusive days that end today. */
function daysSince(days: number): string {
  const d = new Date();
  d.setUTCHours(0, 0, 0, 0);
  d.setUTCDate(d.getUTCDate() - (days - 1));
  return d.toISOString();
}

function playersSince(range: PlayerRange): string {
  return daysSince(PLAYER_RANGES[range].days);
}

/**
 * One environment's daily players and D1/D7/D30 retention, an hour behind.
 * `since` is resolved inside the fetch so the key stays one entry per range.
 */
export function useEnvironmentPlayerActivity(envId: string, range: PlayerRange) {
  const api = useApi();
  return useQuery({
    queryKey: [...keys.environment(envId), "player-activity", range] as const,
    queryFn: async (): Promise<EnvironmentPlayerActivity> =>
      unwrap(
        await api.GET("/v1/environments/{envId}/player-activity", {
          params: { path: { envId }, query: { since: playersSince(range) } },
        }),
      ),
    refetchInterval: 5 * 60_000,
  });
}

/** Retention reads its own sixty days, so D30 can finish whatever range the chart shows. */
const RETENTION_DAYS = 60;

/** The same route as `useEnvironmentPlayerActivity`, asked for the retention window. */
export function useEnvironmentRetention(envId: string) {
  const api = useApi();
  return useQuery({
    queryKey: [...keys.environment(envId), "player-activity", "retention"] as const,
    queryFn: async (): Promise<EnvironmentPlayerActivity> =>
      unwrap(
        await api.GET("/v1/environments/{envId}/player-activity", {
          params: { path: { envId }, query: { since: daysSince(RETENTION_DAYS + 1) } },
        }),
      ),
    refetchInterval: 5 * 60_000,
  });
}

/** The first UTC day of a range ending yesterday, as a date-time at UTC midnight. */
function insightsSince(range: PlayerRange): string {
  const d = new Date();
  d.setUTCHours(0, 0, 0, 0);
  d.setUTCDate(d.getUTCDate() - PLAYER_RANGES[range].days);
  return d.toISOString().replace(/\.\d{3}Z$/, "Z");
}

/**
 * An environment's daily totals from one of the studio's analytics sources. With
 * no `range` the server's default window answers; with one, the window starts at
 * the first day of that range, resolved inside the fetch like `since` above.
 */
export function useInsightsDaily(
  envId: string,
  source: string | undefined,
  range?: PlayerRange,
) {
  const api = useApi();
  return useQuery({
    queryKey: [
      ...keys.environment(envId),
      "insights-daily",
      source,
      ...(range ? [range] : []),
    ] as const,
    enabled: source !== undefined,
    queryFn: async (): Promise<InsightsDaily> =>
      unwrap(
        await api.GET("/v1/environments/{envId}/insights/daily", {
          params: {
            path: { envId },
            query: {
              source: source as string,
              ...(range ? { since: insightsSince(range) } : {}),
            },
          },
        }),
      ),
    refetchInterval: 10 * 60_000,
  });
}

/** The analytics sources this deployment offers and where an environment stands with each. */
export function useInsightsSources(envId: string) {
  const api = useApi();
  return useQuery({
    queryKey: [...keys.environment(envId), "insights-sources"] as const,
    queryFn: async (): Promise<InsightsSource[]> =>
      unwrap(
        await api.GET("/v1/environments/{envId}/insights/sources", {
          params: { path: { envId } },
        }),
      ).sources,
  });
}

/** Proves an environment's connection to an analytics source works. */
export function useCheckSource(envId: string) {
  const api = useApi();
  return useMutation({
    mutationFn: async (vendor: string): Promise<InsightsCheck> =>
      unwrap(
        await api.POST("/v1/environments/{envId}/insights/sources/{vendor}/check", {
          params: { path: { envId, vendor } },
        }),
      ),
  });
}

/** Rule outcomes over a span, or with `days` over that many inclusive UTC days ending today. */
export function useRuleActivity(envId: string, span: ActivitySpan, days?: number) {
  const api = useApi();
  return useQuery({
    queryKey: [...keys.environment(envId), "rule-activity", days ?? span] as const,
    queryFn: async (): Promise<RuleActivity> =>
      unwrap(
        await api.GET("/v1/environments/{envId}/rules/activity", {
          params: {
            path: { envId },
            query: { since: days === undefined ? since(span) : daysSince(days) },
          },
        }),
      ),
    refetchInterval: days === undefined ? ACTIVITY_REFRESH : 5 * 60_000,
  });
}

export function useCurrencyMovement(envId: string, span: ActivitySpan) {
  const api = useApi();
  return useQuery({
    queryKey: [...keys.environment(envId), "currency-movement", span] as const,
    queryFn: async (): Promise<CurrencyMovementReport> =>
      unwrap(
        await api.GET("/v1/environments/{envId}/currencies/movement", {
          params: { path: { envId }, query: { since: since(span) } },
        }),
      ),
    refetchInterval: ACTIVITY_REFRESH,
  });
}

// ------------------------------------------------------------- economy health

/**
 * How far back Economy health looks, and why the choices are wider than
 * Overview's.
 *
 * Overview asks "is anything happening", which is an hour or a day. This asks
 * "are the two halves of my economy drifting apart", which nothing shorter than
 * a week can show: a game with a daily rule and a weekly sink looks like
 * runaway inflation on any Tuesday. Ninety days is where the choices stop
 * because it is where the server stops — a wider period is answered for ninety
 * anyway, and the report says so.
 */
export type HealthSpan = "week" | "month" | "quarter";

/**
 * Two spellings, for `ACTIVITY_SPANS`' reason one screen along: `words`
 * finishes a sentence — "3,150 more is in players' hands than at the start of
 * the last 7 days" — and `short` labels the control that picks it, where the
 * long form repeated three times is a segmented group wider than the column
 * it sits in.
 */
export const HEALTH_SPANS: Record<HealthSpan, { ms: number; words: string; short: string }> = {
  week: { ms: 7 * 24 * 3_600_000, words: "the last 7 days", short: "7 days" },
  month: { ms: 30 * 24 * 3_600_000, words: "the last 30 days", short: "30 days" },
  quarter: { ms: 90 * 24 * 3_600_000, words: "the last 90 days", short: "90 days" },
};

/**
 * Every environment's issuance and spend over one window, in one request.
 *
 * One request rather than one per environment, and that is the property the
 * screen is built on rather than a saving. Two requests that each mean "up to
 * now" resolve "now" twice, so the two columns would cover two different periods
 * and part of the difference between them would be the clock.
 *
 * No refetch interval, unlike Overview's panels. Nobody watches a thirty-day
 * total change, and a report that reanimates under the reader is harder to read
 * than one that is a moment old.
 */
export function useEconomyHealth(projectId: string, span: HealthSpan) {
  const api = useApi();
  return useQuery({
    queryKey: [...keys.project(projectId), "economy-health", span] as const,
    queryFn: async (): Promise<EconomyHealthReport> =>
      unwrap(
        await api.GET("/v1/projects/{projectId}/economy/health", {
          params: {
            path: { projectId },
            // Resolved inside the fetch rather than at render, for the reason
            // the activity reads do it: a timestamp in the query key is a new
            // key on every render and a cache that never hits.
            query: { since: new Date(Date.now() - HEALTH_SPANS[span].ms).toISOString() },
          },
        }),
      ),
  });
}

// -------------------------------------------------------------- player sign-in

/**
 * How players prove who they are, per environment.
 *
 * The list is the sign-in registry left-joined onto this environment, exactly
 * as the integrations catalogue is — there is no second list of providers
 * anywhere, and a hand-maintained one beside a registry would be two answers to
 * one question.
 */
export function useSignInProviders(envId: string) {
  const api = useApi();
  return useQuery({
    queryKey: keys.signInProviders(envId),
    queryFn: async (): Promise<SignInProviderEntry[]> => {
      const res = await api.GET("/v1/environments/{envId}/sign-in-providers", {
        params: { path: { envId } },
      });
      return unwrap(res).providers;
    },
    enabled: Boolean(envId),
  });
}

/**
 * Configure a provider, or restate its settings.
 *
 * `audiences` and `settings` are both the complete set rather than a patch,
 * because the form renders all of them and submits all of them. A partial write
 * that silently kept the previous value is how a stale client id survives a
 * change with nothing on screen to say why tokens are being refused.
 *
 * A first configuration is enabled. Restating one never re-enables a provider
 * somebody switched off — that is what makes the switch worth pressing when
 * something is wrong.
 */
export function useConfigureSignInProvider(envId: string) {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      provider: string;
      audiences: string[];
      settings: Record<string, string>;
    }) =>
      unwrap(
        await api.PUT("/v1/environments/{envId}/sign-in-providers/{provider}", {
          params: { path: { envId, provider: input.provider } },
          body: { audiences: input.audiences, settings: input.settings },
        }),
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.signInProviders(envId) }),
  });
}

export function useSetSignInProviderEnabled(envId: string) {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { provider: string; enabled: boolean }) =>
      unwrap(
        await api.PUT("/v1/environments/{envId}/sign-in-providers/{provider}/enabled", {
          params: { path: { envId, provider: input.provider } },
          body: { enabled: input.enabled },
        }),
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.signInProviders(envId) }),
  });
}

/**
 * Remove a provider's configuration.
 *
 * Not the same act as switching it off, and for a provider that needs no
 * settings it is the opposite one: guest and email are offered by default, so
 * their configuration exists only to hold the switch and deleting it turns them
 * back on. The screen says which of the two it is about to do.
 */
export function useRemoveSignInProvider(envId: string) {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (provider: string) => {
      await api.DELETE("/v1/environments/{envId}/sign-in-providers/{provider}", {
        params: { path: { envId, provider } },
      });
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.signInProviders(envId) }),
  });
}

// --------------------------------------------------------------- integrations

/**
 * The vendor accounts an environment has connected, and everything that has
 * happened to them.
 *
 * Environment-scoped, and that is not an accident of the URL: a studio connects
 * its own vendor account per environment and pays that vendor directly, so
 * development and production hold different keys and usually different apps.
 * A project-wide read would let a test build spend production ad inventory.
 *
 * Every write below invalidates `keys.integrations`, and the audit key sits
 * underneath it so one invalidation covers both. That is deliberate rather than
 * lazy: an audit trail that lags the change it describes by a refetch is a
 * screen where somebody rotates a key, looks at the record, and does not find
 * what they just did.
 */
export function useIntegrations(envId: string) {
  const api = useApi();
  return useQuery({
    queryKey: keys.integrations(envId),
    queryFn: async (): Promise<IntegrationEntry[]> => {
      const res = await api.GET("/v1/environments/{envId}/integrations", {
        params: { path: { envId } },
      });
      return unwrap(res).integrations;
    },
    enabled: Boolean(envId),
  });
}

export function useIntegrationAudit(envId: string, limit = 100) {
  const api = useApi();
  return useQuery({
    queryKey: [...keys.integrationAudit(envId), limit] as const,
    queryFn: async (): Promise<IntegrationAuditEntry[]> => {
      const res = await api.GET("/v1/environments/{envId}/integrations/audit", {
        params: { path: { envId }, query: { limit } },
      });
      return unwrap(res).entries;
    },
    enabled: Boolean(envId),
  });
}

/**
 * Create a connection, or restate its plain settings.
 *
 * `settings` is the complete set of the adapter's non-secret slots rather than
 * a patch, because the form renders all of them and submits all of them. A
 * partial write that silently kept the previous value is how a stale
 * application id survives a reconnect with nothing on screen to say why the
 * vendor is refusing the call.
 *
 * It does not enable anything. Connecting and enabling are separate acts with
 * separate audit entries, and re-running this must never turn a vendor back on
 * that somebody switched off.
 */
export function useConnectIntegration(envId: string) {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { vendor: string; settings: Record<string, unknown> }) =>
      unwrap(
        await api.PUT("/v1/environments/{envId}/integrations/{vendor}", {
          params: { path: { envId, vendor: input.vendor } },
          body: { settings: input.settings },
        }),
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: sourcesKey(envId) });
      return qc.invalidateQueries({ queryKey: keys.integrations(envId) });
    },
  });
}

/**
 * The other environments of a game that hold a connection to a vendor, which
 * are the ones this environment can copy from. Read from each environment's
 * own integrations, so it shares their cache.
 */
export function useCopySources(projectId: string, envId: string, vendor: string) {
  const api = useApi();
  const environments = useEnvironments(projectId);
  const others = (environments.data ?? []).filter((e) => e.id !== envId);
  const results = useQueries({
    queries: others.map((e) => ({
      queryKey: keys.integrations(e.id),
      queryFn: async (): Promise<IntegrationEntry[]> =>
        unwrap(
          await api.GET("/v1/environments/{envId}/integrations", {
            params: { path: { envId: e.id } },
          }),
        ).integrations,
    })),
  });
  return others.filter((_, i) =>
    results[i]?.data?.some(
      (entry) =>
        entry.vendor === vendor &&
        entry.connection &&
        !entry.connection.orphaned &&
        entry.connection.incomplete.length === 0,
    ),
  );
}

const sourcesKey = (envId: string) => [...keys.environment(envId), "insights-sources"] as const;

/** Connects an environment to a vendor by copying another environment's connection, switched off. */
export function useCopyIntegration(envId: string) {
  const api = useApi();
  const qc = useQueryClient();
  const refresh = () => {
    void qc.invalidateQueries({ queryKey: keys.integrations(envId) });
    void qc.invalidateQueries({ queryKey: sourcesKey(envId) });
  };
  return useMutation({
    mutationFn: async (input: { vendor: string; fromEnvironmentId: string }) =>
      unwrap(
        await api.POST("/v1/environments/{envId}/integrations/{vendor}/copy", {
          params: { path: { envId, vendor: input.vendor } },
          body: { from_environment_id: input.fromEnvironmentId },
        }),
      ),
    onSuccess: refresh,
    onError: (_error, input) => {
      refresh();
      void qc.invalidateQueries({ queryKey: keys.integrations(input.fromEnvironmentId) });
    },
  });
}

export function useSetIntegrationEnabled(envId: string) {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { vendor: string; enabled: boolean }) =>
      unwrap(
        await api.PUT("/v1/environments/{envId}/integrations/{vendor}/enabled", {
          params: { path: { envId, vendor: input.vendor } },
          body: { enabled: input.enabled },
        }),
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: sourcesKey(envId) });
      return qc.invalidateQueries({ queryKey: keys.integrations(envId) });
    },
  });
}

export function useDisconnectIntegration(envId: string) {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (vendor: string) => {
      await api.DELETE("/v1/environments/{envId}/integrations/{vendor}", {
        params: { path: { envId, vendor } },
      });
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: sourcesKey(envId) });
      return qc.invalidateQueries({ queryKey: keys.integrations(envId) });
    },
  });
}

/**
 * Store one of a studio's vendor secrets.
 *
 * There is no matching read and there must never be one. The value is
 * envelope-encrypted before it reaches storage and no endpoint in the API
 * returns it, so this mutation's own input is the last place the plaintext
 * exists in this process — which is why nothing here keeps it, logs it or puts
 * it in a cache key.
 */
export function usePutIntegrationCredential(envId: string) {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { vendor: string; slot: string; value: string }) => {
      await api.PUT("/v1/environments/{envId}/integrations/{vendor}/credentials/{slot}", {
        params: { path: { envId, vendor: input.vendor, slot: input.slot } },
        body: { value: input.value },
      });
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.integrations(envId) }),
  });
}

export function useClearIntegrationCredential(envId: string) {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { vendor: string; slot: string }) => {
      await api.DELETE("/v1/environments/{envId}/integrations/{vendor}/credentials/{slot}", {
        params: { path: { envId, vendor: input.vendor, slot: input.slot } },
      });
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.integrations(envId) }),
  });
}

// -------------------------------------------------------------------- the team

/**
 * Who is in an organisation, and who has been asked.
 *
 * Org-scoped rather than project-scoped, which is why these live outside the
 * project shell: a membership grants a role across a studio, and putting the
 * team behind a project switcher would suggest otherwise.
 */
export function useMembers(orgId: string) {
  const api = useApi();
  return useQuery({
    queryKey: keys.members(orgId),
    queryFn: () => membersBody(api, orgId),
    select: (body) => body.members,
  });
}

/**
 * How many people this organisation holds against what its plan allows.
 *
 * Reads the same response as `useMembers`, on the same query key, with its own
 * `select` — one request serves both, and the two can never disagree about how
 * many people are in the studio. `Team` and `Plan` both read this rather than
 * counting `useMembers`' own list themselves, which is the "read, never
 * worked out" house rule: `can_invite` and `over_limit` arrive derived and a
 * screen comparing `used` to `limit` on its own would be a second, driftable
 * copy of that decision.
 */
export function useMemberSeats(orgId: string) {
  const api = useApi();
  return useQuery({
    queryKey: keys.members(orgId),
    queryFn: () => membersBody(api, orgId),
    select: (body) => body.seats,
  });
}

/**
 * Seats bought past the plan's included members. The answer is the same
 * `MemberSeats` the member list carries, written straight into that cache so
 * Invite opens the moment a seat is bought; the entitlement is refetched
 * because `MemberCap` reads the bought count too.
 */
export function useSetExtraSeats(orgId: string) {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (extraSeats: number): Promise<MemberSeats> =>
      unwrap(
        await api.PUT("/v1/orgs/{orgId}/entitlement/seats", {
          params: { path: { orgId }, header: { "Idempotency-Key": idempotencyKey() } },
          body: { extra_seats: extraSeats },
        }),
      ),
    onSuccess: (seats) => {
      qc.setQueryData<{ members: Member[]; seats?: MemberSeats }>(
        keys.members(orgId),
        (body) => (body ? { ...body, seats } : body),
      );
      void qc.invalidateQueries({ queryKey: keys.entitlement(orgId) });
    },
  });
}

async function membersBody(
  api: ReturnType<typeof useApi>,
  orgId: string,
): Promise<{ members: Member[]; seats?: MemberSeats }> {
  return unwrap(await api.GET("/v1/orgs/{orgId}/members", { params: { path: { orgId } } }));
}

export function useInvitations(orgId: string) {
  const api = useApi();
  return useQuery({
    queryKey: keys.invitations(orgId),
    queryFn: async (): Promise<Invitation[]> =>
      unwrap(await api.GET("/v1/orgs/{orgId}/invitations", { params: { path: { orgId } } }))
        .invitations,
  });
}

/**
 * The role catalogue, so a form can offer a choice and say what each one grants.
 *
 * Platform-wide rather than per-org — roles are rows with a unique slug and
 * every studio picks from the same list — so this is cached hard. It changes
 * when a deployment adds a role, which is on the order of never.
 */
export function useRoles() {
  const api = useApi();
  return useQuery({
    queryKey: keys.roles,
    queryFn: async (): Promise<Role[]> => unwrap(await api.GET("/v1/roles")).roles,
    staleTime: 5 * 60_000,
  });
}

/**
 * Inviting a teammate, which is also how a failed send is retried.
 *
 * There is no separate resend: calling this again for the same address reissues
 * the link and mails it, and the previous link stops working. Two ways to do
 * one thing is one too many, and the case that needs a resend most is the one
 * where the first attempt errored.
 */
export function useCreateInvitation(orgId: string) {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: {
      email: string;
      role: string;
      project_id?: string;
    }): Promise<Invitation> =>
      unwrap(
        await api.POST("/v1/orgs/{orgId}/invitations", { params: { path: { orgId } }, body }),
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: keys.invitations(orgId) });
      // A pending invitation holds a seat, so sending one can move `seats`
      // (which shares `keys.members` with the member list) from can-invite to
      // can't.
      void qc.invalidateQueries({ queryKey: keys.members(orgId) });
    },
  });
}

export function useRevokeInvitation(orgId: string) {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (invitationId: string): Promise<Invitation> =>
      unwrap(
        await api.DELETE("/v1/orgs/{orgId}/invitations/{invitationId}", {
          params: { path: { orgId, invitationId } },
        }),
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: keys.invitations(orgId) });
      // Withdrawing a pending invitation can free the seat it held.
      void qc.invalidateQueries({ queryKey: keys.members(orgId) });
    },
  });
}

/**
 * Redeeming an invitation link — `/invitations/$token`'s one call.
 *
 * Outside an organisation's own hooks deliberately: the caller is not in the
 * org yet, so there is nothing here scoped to an `orgId`. Invalidates the
 * viewer on success, `already_member` or not, so the studio switcher and
 * `IndexScreen`'s membership list carry the org the moment this resolves
 * rather than on the next unrelated refetch.
 */
export function useAcceptInvitation() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (token: string): Promise<AcceptedInvitation> =>
      unwrap(await api.POST("/v1/invitations/accept", { body: { token } })),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: keys.viewer });
    },
  });
}

/**
 * Changing what a teammate may do.
 *
 * Also invalidates the viewer, because the person doing this can be the person
 * it happens to: an owner who makes themselves a viewer should see the console
 * they now have rather than the one they had.
 */
export function useSetMemberRole(orgId: string) {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ userId, role }: { userId: string; role: string }): Promise<Member> =>
      unwrap(
        await api.PATCH("/v1/orgs/{orgId}/members/{userId}", {
          params: { path: { orgId, userId } },
          body: { role },
        }),
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: keys.members(orgId) });
      void qc.invalidateQueries({ queryKey: keys.viewer });
    },
  });
}

export function useRemoveMember(orgId: string) {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    // No unwrap: a 204 has no body, and the client middleware throws on any
    // non-ok response, so awaiting is the whole check.
    mutationFn: async (userId: string): Promise<void> => {
      await api.DELETE("/v1/orgs/{orgId}/members/{userId}", {
        params: { path: { orgId, userId } },
      });
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: keys.members(orgId) });
      void qc.invalidateQueries({ queryKey: keys.viewer });
    },
  });
}

// ---------------------------------------------------------------- ad networks

/**
 * The rewarded-ad networks this environment can serve, and how far each one is
 * set up.
 *
 * Every network this deployment has an adapter for, not only the connected
 * ones — because the callback URL a developer has to paste into the vendor's
 * dashboard is the first thing they need and there is nothing to show it beside
 * until they have pasted it. decisions/0008 calls the missing version of that
 * screen the cheapest item in v1's entire gap ledger.
 *
 * Environment-scoped for the reason the integrations catalogue is: a callback
 * URL points at one environment, and one connection shared across environments
 * would let a development build spend a production ad budget.
 */
export function useAdNetworks(envId: string) {
  const api = useApi();
  return useQuery({
    queryKey: keys.adNetworks(envId),
    queryFn: async (): Promise<AdNetworkStatus[]> => {
      const res = await api.GET("/v1/environments/{envId}/ad-networks", {
        params: { path: { envId } },
      });
      // The generated type promises non-null arrays; the server sends `null`
      // for one it considers empty (nothing missing, an adapter with no
      // slots). Normalized once here so nothing downstream has to guard it.
      return unwrap(res).networks.map((n) => ({
        ...n,
        missing: n.missing ?? [],
        slots: n.slots ?? [],
      }));
    },
    enabled: Boolean(envId),
  });
}

/**
 * Switch a network on or off, and choose test or real inventory.
 *
 * `slots` is deliberately not sent. This operation takes references and refuses
 * values, and the references are written by the server when a credential is
 * stored — so a screen that sent them would be a screen that could send a
 * secret instead. Leaving the field out is what makes that impossible from
 * here rather than merely discouraged.
 */
export function useConnectAdNetwork(envId: string) {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      network: string;
      enabled: boolean;
      testMode: boolean;
      settings?: Record<string, string>;
    }) =>
      unwrap(
        await api.PUT("/v1/environments/{envId}/ad-networks/{network}", {
          params: { path: { envId, network: input.network } },
          body: { enabled: input.enabled, test_mode: input.testMode, settings: input.settings },
        }),
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.adNetworks(envId) }),
  });
}

/**
 * Store one of a network's credentials.
 *
 * There is no matching read and there must never be one. The value is encrypted
 * before it reaches storage and no endpoint returns it, so this mutation's own
 * input is the last place the plaintext exists in this process — which is why
 * nothing here keeps it, logs it or puts it in a cache key.
 *
 * The response is the network's status, which the screen renders directly: it
 * is the one response on this path that could be made to carry a secret, and it
 * has no field for one.
 */
export function usePutAdNetworkCredential(envId: string) {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { network: string; slot: string; value: string }) =>
      unwrap(
        await api.PUT("/v1/environments/{envId}/ad-networks/{network}/credentials/{slot}", {
          params: { path: { envId, network: input.network, slot: input.slot } },
          body: { value: input.value },
        }),
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.adNetworks(envId) }),
  });
}

export function useClearAdNetworkCredential(envId: string) {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { network: string; slot: string }) =>
      unwrap(
        await api.DELETE("/v1/environments/{envId}/ad-networks/{network}/credentials/{slot}", {
          params: { path: { envId, network: input.network, slot: input.slot } },
        }),
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.adNetworks(envId) }),
  });
}

/**
 * Exercise one connection and report what each check found.
 *
 * A mutation rather than a query, and not because it writes anything: it makes
 * a request to a third party on the studio's own account, and that is something
 * a person asks for rather than something a screen does on render. A query
 * would refetch it on every focus.
 */
export function useTestAdNetwork(envId: string) {
  const api = useApi();
  return useMutation({
    mutationFn: async (network: string): Promise<AdNetworkTest> =>
      unwrap(
        await api.POST("/v1/environments/{envId}/ad-networks/{network}/test", {
          params: { path: { envId, network } },
        }),
      ),
  });
}

/**
 * Redemptions, filtered by state.
 *
 * Still environment-scoped after `decisions/0035` removed the studio's own
 * voucher catalogue and its writes: what this reads is which of *this
 * environment's* players paid the Magicweave Shop and has not received, which
 * is a support question about players, and players live in environments.
 *
 * The filter is the reason this exists rather than an option on it. The
 * question somebody arrives with is "which redemptions took a player's currency
 * and have not delivered", and that is three states — so the screen asks for
 * them by name rather than reading everything and counting on the client.
 */
export function useRedemptions(envId: string, state: RedemptionState | "") {
  const api = useApi();
  return useQuery({
    queryKey: keys.redemptions(envId, state),
    queryFn: async (): Promise<EnvironmentRedemption[]> => {
      const res = await api.GET("/v1/environments/{envId}/redemptions", {
        params: { path: { envId }, query: state ? { state } : {} },
      });
      return unwrap(res).redemptions;
    },
    enabled: Boolean(envId),
  });
}

/**
 * What the networks said they paid, beside what we verified.
 *
 * The two counts come from different systems and the comparison is the point:
 * our own rule count is a number we produced about ourselves, and the
 * vendor's report is the only independent measurement a studio has to check it
 * against.
 */
export function useAdRevenue(envId: string, network?: string) {
  const api = useApi();
  return useQuery({
    queryKey: [...keys.adRevenue(envId), network ?? ""] as const,
    queryFn: async (): Promise<AdRevenue> => {
      const res = await api.GET("/v1/environments/{envId}/ad-revenue", {
        params: { path: { envId }, query: network ? { network } : {} },
      });
      const revenue = unwrap(res);
      // Same nullable-empty-array quirk as `useAdNetworks`: a range with
      // nothing to report can come back as `days: null`.
      return { ...revenue, days: revenue.days ?? [] };
    },
    enabled: Boolean(envId),
  });
}

// -------------------------------------------------------------------- metering

/**
 * What a studio's plan includes, and what has been used of it.
 *
 * Organisation-scoped, like the team, and for the same reason: a plan covers a
 * whole studio, and reading it behind a project switcher would suggest it could
 * differ from one project to the next. It cannot.
 *
 * Read the capabilities from here and never from `usePlans()`. A studio may be
 * pinned to an older version of the same plan than the catalogue offers — the
 * server keeps them apart deliberately, and v1's own billing screen conflates
 * them and shows a cap that is not the one being enforced.
 *
 * `keepPrevious` is for the top strip and for nothing else. The chip lives in
 * the frame rather than in a screen, so an organisation switch re-keys a query
 * that is on screen the whole time, and the default behaviour empties it for as
 * long as the read takes — a chip that vanishes and comes back on the two
 * screens somebody opened *because* they were thinking about credits. Holding
 * the previous answer keeps it in place, and the caller dims what it is holding
 * until `isPlaceholderData` clears. A screen must not pass this: the plan
 * screen showing one organisation's allowance under another's heading is worth
 * more than the flicker it would save.
 */
export function useEntitlement(orgId: string, options?: { keepPrevious?: boolean }) {
  const api = useApi();
  return useQuery({
    queryKey: keys.entitlement(orgId),
    queryFn: async (): Promise<Entitlement> =>
      unwrap(await api.GET("/v1/orgs/{orgId}/entitlement", { params: { path: { orgId } } })),
    enabled: Boolean(orgId),
    ...(options?.keepPrevious ? { placeholderData: keepPreviousData } : {}),
  });
}

/**
 * Whether an organisation is in the MagicCoin network, and how far along.
 *
 * One query, one key, shared by every screen that has to gate on membership —
 * the sidebar, the Economy panel's synthesised MagicCoin row, and the network
 * shelf all read this hook rather than each fetching `/network/membership`
 * themselves, on the same argument `useEntitlement` above makes: two readers
 * of the same fact on two keys can disagree about which organisation is a
 * member for as long as their caches are out of step.
 *
 * `keepPrevious` follows `useAllowanceChip`'s rule and not `useEntitlement`'s
 * default: opt in only for chrome that must not flicker or vanish across an
 * org switch (a sidebar badge), and call this plain wherever the answer is
 * treated as ground truth (a gate that decides what renders, a card that
 * states current standing) — those must not hold a previous organisation's
 * membership on screen while a new one is still loading.
 */
export function useMembership(orgId: string, options?: { keepPrevious?: boolean }) {
  const api = useApi();
  return useQuery({
    queryKey: keys.membership(orgId),
    queryFn: async (): Promise<NetworkMembership> =>
      unwrap(
        await api.GET("/v1/orgs/{orgId}/network/membership", { params: { path: { orgId } } }),
      ),
    enabled: Boolean(orgId),
    ...(options?.keepPrevious ? { placeholderData: keepPreviousData } : {}),
  });
}

/**
 * What staff are currently offering the network's players — a member studio's
 * own read of the shelf.
 *
 * One catalogue, one stock count, shared by every member — this is not a
 * per-studio copy, so there is nothing here that could disagree between two
 * studios reading it at once.
 *
 * The whole body rather than the listing array, because `decisions/0035` put
 * a card on the response: the Magicweave Shop is one more card on the studio's
 * own wall, and the name on that card is staff's to write, not ours to invent.
 */
export function useNetworkShelf(orgId: string) {
  const api = useApi();
  return useQuery({
    queryKey: keys.networkShelf(orgId),
    queryFn: async (): Promise<{ vouchers: NetworkShelfListing[]; card?: NetworkShopCard }> =>
      unwrap(await api.GET("/v1/orgs/{orgId}/network/shelf", { params: { path: { orgId } } })),
    enabled: Boolean(orgId),
  });
}

/**
 * The studio's till: whether it accepts MagicCoins against its own goods, and
 * up to how much a month — `decisions/0035`'s acceptance switch.
 *
 * Off is the default and `decided: false` is a third state the screen has to
 * keep apart from it: nobody choosing yet and somebody choosing off read the
 * same at the spend and differently to the person looking at the switch.
 */
export function useNetworkAcceptanceSetting(orgId: string) {
  const api = useApi();
  return useQuery({
    queryKey: keys.networkAcceptanceSetting(orgId),
    queryFn: async (): Promise<NetworkAcceptanceSetting> =>
      unwrap(
        await api.GET("/v1/orgs/{orgId}/network/acceptance/setting", {
          params: { path: { orgId } },
        }),
      ),
    enabled: Boolean(orgId),
  });
}

/**
 * Set the till.
 *
 * Enforced at the spend rather than at publish, so an opt-out stops coin
 * sales at once with no republish — which is the whole reason it is a row a
 * person flips and not a field in a release document. The answer is written
 * back into the cache the way `useApply` writes a membership, so the switch
 * never shows the old position after the server accepted the new one.
 */
export function useSetNetworkAcceptanceSetting(orgId: string) {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: {
      enabled: boolean;
      cap_coins_per_month?: number | null;
      note?: string;
    }): Promise<NetworkAcceptanceSetting> =>
      unwrap(
        await api.PUT("/v1/orgs/{orgId}/network/acceptance/setting", {
          params: { path: { orgId } },
          body,
        }),
      ),
    onSuccess: (setting) => {
      qc.setQueryData(keys.networkAcceptanceSetting(orgId), setting);
    },
  });
}

/**
 * Where this studio stands on minting Shared Loyalty coins this month.
 *
 * Coins are billed in money at the plan's `coin_price`, one at a time as rules
 * hand them out, and added to the month's invoice — so `used` against `cap` is
 * the bill so far and how far it may still run. `price` is the plan's own
 * figure and answers whether the plan mints at all, which is why the joining
 * screen reads it too: it needs no membership, only `org:read`.
 */
export function useCoinStanding(orgId: string) {
  const api = useApi();
  return useQuery({
    queryKey: keys.coinStanding(orgId),
    queryFn: async (): Promise<CoinStanding> =>
      unwrap(await api.GET("/v1/orgs/{orgId}/network/coins", { params: { path: { orgId } } })),
    enabled: Boolean(orgId),
  });
}

/**
 * Set, or clear with `null`, the studio's own monthly coin limit.
 *
 * Below the plan's ceiling only — above it, or on a plan that does not mint,
 * is a `402` whose message the screen shows as it arrives. The answer is the
 * whole new standing, so it lands in the cache at once; the invalidation behind
 * it is for `used`, which a game can move between the write and the next read.
 */
export function useSetCoinLimit(orgId: string) {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (limit: number | null): Promise<CoinStanding> =>
      unwrap(
        await api.PUT("/v1/orgs/{orgId}/network/coin-limit", {
          params: { path: { orgId } },
          body: { limit },
        }),
      ),
    onSuccess: (standing) => {
      qc.setQueryData(keys.coinStanding(orgId), standing);
      void qc.invalidateQueries({ queryKey: keys.coinStanding(orgId) });
    },
  });
}

/**
 * What accepting somebody else's coin did for this studio.
 *
 * The acceptor's side of `useMembership`'s issuer counterpart — `NetworkQuota`
 * reads what this studio may issue, this reads what taking in somebody else's
 * coins earned it.
 */
export function useNetworkAcceptance(orgId: string) {
  const api = useApi();
  return useQuery({
    queryKey: keys.networkAcceptance(orgId),
    queryFn: async (): Promise<NetworkAcceptance> =>
      unwrap(
        await api.GET("/v1/orgs/{orgId}/network/acceptance", { params: { path: { orgId } } }),
      ),
    enabled: Boolean(orgId),
  });
}

/**
 * The current period's usage, broken down by meter.
 *
 * A meter with no activity is absent rather than zero, so an empty list means
 * nothing has been metered this month and not that the read failed.
 */
export function usePlanUsage(orgId: string) {
  const api = useApi();
  return useQuery({
    queryKey: keys.planUsage(orgId),
    queryFn: async (): Promise<PlanUsage> =>
      unwrap(await api.GET("/v1/orgs/{orgId}/usage", { params: { path: { orgId } } })),
    enabled: Boolean(orgId),
  });
}

/**
 * Every player the organisation has ever signed up, against its plan's
 * lifetime limit, split by project and environment.
 *
 * A read of identity's counter rather than anything summed here: the server's
 * `onboarded` is the number the sign-up gate compares, so the screen and the
 * gate cannot disagree about whether there is room.
 */
export function usePlayerUsage(orgId: string) {
  const api = useApi();
  return useQuery({
    queryKey: keys.playerUsage(orgId),
    queryFn: async (): Promise<PlayerUsage> =>
      unwrap(await api.GET("/v1/orgs/{orgId}/players/usage", { params: { path: { orgId } } })),
    enabled: Boolean(orgId),
  });
}

/**
 * Requests this month and player storage, against the plan's guardrails.
 *
 * The server's figures, read as they are: `paced` is the edge's own state, not
 * `used >= allowance` worked out here, and a `null` count means not measured.
 */
export function useUsageGuardrails(orgId: string) {
  const api = useApi();
  return useQuery({
    queryKey: keys.usageGuardrails(orgId),
    queryFn: async (): Promise<UsageGuardrails> =>
      unwrap(
        await api.GET("/v1/orgs/{orgId}/usage/guardrails", { params: { path: { orgId } } }),
      ),
    enabled: Boolean(orgId),
  });
}

/**
 * The same period, day by day, and the forecast over it.
 *
 * A second request rather than more fields on the one above, because the two
 * answers are different sizes: a month of days is a chart's worth of rows and
 * the totals are read by the strip on every screen. They come from the same
 * maintained rows, so a screen showing both is showing one set of facts twice
 * and not two readings that can disagree.
 *
 * Nothing here fills a gap, resamples a series or extends one. `days` is what
 * the server maintained, and the forecast is the server's — see
 * `PlanProjection`.
 */
export function usePlanUsageHistory(orgId: string) {
  const api = useApi();
  return useQuery({
    queryKey: keys.planUsageHistory(orgId),
    queryFn: async (): Promise<PlanUsageHistory> =>
      unwrap(await api.GET("/v1/orgs/{orgId}/usage/history", { params: { path: { orgId } } })),
    enabled: Boolean(orgId),
  });
}

/**
 * The catalogue. Platform-wide rather than per-organisation, so it is cached
 * hard: it changes when somebody publishes a plan, which is on the order of
 * never.
 */
export function usePlans() {
  const api = useApi();
  return useQuery({
    queryKey: keys.plans,
    queryFn: async (): Promise<Plan[]> => unwrap(await api.GET("/v1/plans")).plans,
    staleTime: 5 * 60_000,
  });
}

/**
 * The overage switch, and the ceiling that comes with it.
 *
 * The two are set together because either one alone is already the cap — a
 * switch on with a ceiling of nought buys nothing, and a ceiling with the switch
 * off spends nothing. `decisions/0025` decision 6.
 *
 * **No plan is refused here and none is checked for.** A plan that prices
 * nothing past its allowance takes the switch and the ceiling and buys nought
 * credits with them, which reads back as `capped` with a remedy of
 * `raise_plan`. That is the whole of how a free tier is excluded, and a client
 * that declined to send the call would be inventing the rule the decision
 * refuses to write.
 *
 * The answer is the entitlement the change produced, so it is written straight
 * into the cache the chip and this screen share rather than invalidated: a
 * refetch here would be asking the server for what it has just finished
 * saying, and the window before the answer arrives is a window where the
 * screen shows the old ceiling.
 */
export function useSetOverage(orgId: string) {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (sw: OverageSwitch): Promise<Entitlement> =>
      unwrap(
        await api.PUT("/v1/orgs/{orgId}/entitlement/overage", {
          params: { path: { orgId }, header: { "Idempotency-Key": idempotencyKey() } },
          body: sw,
        }),
      ),
    onSuccess: (entitlement) => {
      qc.setQueryData(keys.entitlement(orgId), entitlement);
    },
  });
}

/**
 * What one metered action costs, per meter, and which price list said so.
 *
 * Platform-wide rather than per-organisation — one active card for everybody,
 * `decisions/0025` decision 4 — so it is one query on one key however many
 * charging controls a screen puts on itself.
 *
 * Cached like the catalogue but not as hard. A card is not grandfathered and has
 * no undo, so a price staff changed is in force for everybody at once and this
 * is the read that has to notice: a console holding a stale figure quotes a
 * price nobody will be charged, which is the failure `SHIPPED_RATES` was
 * deleted for.
 */
export function useRateCard() {
  const api = useApi();
  return useQuery({
    queryKey: keys.rateCard,
    queryFn: async (): Promise<RateCard> => unwrap(await api.GET("/v1/rate-card")),
    staleTime: 60_000,
  });
}

/**
 * Changing plan.
 *
 * An upgrade lands now; a downgrade is scheduled for the start of the next
 * period and comes back as `pending` until then. The screen reads `immediate`
 * off the response rather than comparing prices itself, because which direction
 * a change is depends on the version the studio is pinned to and only the server
 * knows that.
 *
 * The entitlement is invalidated either way, including for a scheduled change —
 * the pending block is part of it.
 */
export function useChangePlan(orgId: string) {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (code: string): Promise<PlanChange> =>
      unwrap(
        await api.PUT("/v1/orgs/{orgId}/entitlement", {
          params: { path: { orgId }, header: { "Idempotency-Key": idempotencyKey() } },
          body: { code },
        }),
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: keys.entitlement(orgId) });
    },
  });
}

/**
 * The billing snapshot: the tax profile, the mandate, any plan change in
 * flight, and every invoice — one read for the reason `useEntitlement`
 * gives about its own halves.
 *
 * **Polls itself while a plan change is in flight.** `in_flight.state` of
 * `requested` or `charged` is the window between sending a studio to
 * Razorpay and the webhook landing — `decisions/0027` — and that is exactly
 * what a studio is looking at right after the redirect back from `pay_url`.
 * Polling stops the moment the state turns terminal (`applied`, `failed`,
 * `held`) or `in_flight` disappears, so a resolved change does not keep this
 * screen refetching forever.
 *
 * `poll` keeps it polling regardless, for checkout while it waits on a
 * payment: the read it starts from predates the change it is waiting for.
 */
export function useBilling(orgId: string, options?: { poll?: boolean }) {
  const api = useApi();
  return useQuery({
    queryKey: keys.billing(orgId),
    queryFn: async (): Promise<BillingRecord> =>
      unwrap(await api.GET("/v1/orgs/{orgId}/billing", { params: { path: { orgId } } })),
    enabled: Boolean(orgId),
    refetchInterval: (query) => {
      if (options?.poll) return 3000;
      const state = query.state.data?.in_flight?.state;
      return state === "requested" || state === "charged" ? 3000 : false;
    },
  });
}

/**
 * One invoice, in full — `getBilling`'s list carries no lines.
 *
 * `enabled` on a real id so a detail dialog can mount before anybody has
 * chosen an invoice to look at, the same arrangement every hook below that
 * takes an optional id already uses with a `null` one.
 */
export function useInvoice(orgId: string, invoiceId: string | null) {
  const api = useApi();
  return useQuery({
    queryKey: keys.invoice(orgId, invoiceId ?? ""),
    queryFn: async (): Promise<Invoice> =>
      unwrap(
        await api.GET("/v1/orgs/{orgId}/invoices/{invoiceId}", {
          params: { path: { orgId, invoiceId: invoiceId as string } },
        }),
      ),
    enabled: Boolean(orgId) && invoiceId !== null,
  });
}

/**
 * The tax profile alone, to prefill the form — `getTaxProfile`, added
 * alongside `billing_email` rather than read out of `getBilling`: "the
 * console reads this to prefill the tax-profile form... rather than reaching
 * into `getBilling` for one field."
 *
 * `404` is the common case, not a failure — most organisations have never
 * saved one — so it resolves to `null` rather than landing in `.error`. The
 * route's own docs say why this has to be more than a blank body: the
 * checkout gate needs "never filled in" told apart from a transport error,
 * and a `null` that came back from an actual 404 is exactly that told apart
 * from a query still pending or one that failed for another reason.
 */
export function useTaxProfile(orgId: string) {
  const api = useApi();
  return useQuery({
    queryKey: keys.taxProfile(orgId),
    queryFn: async (): Promise<BillingTaxProfile | null> => {
      try {
        return unwrap(
          await api.GET("/v1/orgs/{orgId}/tax-profile", { params: { path: { orgId } } }),
        );
      } catch (err) {
        if (isApiError(err) && err.status === 404) return null;
        throw err;
      }
    },
    enabled: Boolean(orgId),
  });
}

/**
 * Save the tax profile: a whole write, not a patch — see `TaxProfileSpec`.
 *
 * The answer is written straight into both caches that hold a copy — this
 * form's own `taxProfile` read and `getBilling`'s `tax_profile` field, which
 * the checkout gate on `ChangeDialog` still reads — rather than invalidated,
 * on `useSetOverage`'s reasoning: a refetch here would ask the server for
 * what it just finished saying, and the window before it arrived would show
 * the old profile on the form that just saved and on the gate beside it.
 */
export function useSaveTaxProfile(orgId: string) {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (spec: TaxProfileSpec) =>
      unwrap(
        await api.PUT("/v1/orgs/{orgId}/tax-profile", {
          params: { path: { orgId } },
          body: spec,
        }),
      ),
    onSuccess: (profile) => {
      qc.setQueryData(keys.taxProfile(orgId), profile);
      qc.setQueryData(keys.billing(orgId), (current: BillingRecord | undefined) =>
        current ? { ...current, tax_profile: profile } : current,
      );
    },
  });
}

/**
 * The AI credit packs this organisation can buy, priced in its billing
 * currency, with the packs it still holds and its newest orders.
 *
 * Refused with a 403 on a plan that sells none (Free) and a 422 with no
 * billing profile; both arrive as errors for the sheet to answer in its own
 * words. Not retried: neither refusal changes on a second ask.
 *
 * `poll` keeps it polling while a paid order waits on Razorpay's webhook —
 * the grant lands there, never on the request that opened the order.
 */
export function useCreditPacks(orgId: string, options?: { poll?: boolean; enabled?: boolean }) {
  const api = useApi();
  return useQuery({
    queryKey: keys.creditPacks(orgId),
    queryFn: async (): Promise<CreditPackQuote> =>
      unwrap(await api.GET("/v1/orgs/{orgId}/credit-packs", { params: { path: { orgId } } })),
    enabled: Boolean(orgId) && (options?.enabled ?? true),
    retry: false,
    refetchInterval: options?.poll ? 3000 : false,
  });
}

/**
 * Open an order for one pack, named by its credits — the amount is the
 * server's, repriced from the active card. Nothing is charged here.
 */
export function useBuyCreditPack(orgId: string) {
  const api = useApi();
  return useMutation({
    mutationFn: async (credits: number): Promise<CreditPackCheckout> =>
      unwrap(
        await api.POST("/v1/orgs/{orgId}/credit-packs/orders", {
          params: { path: { orgId }, header: { "Idempotency-Key": idempotencyKey() } },
          body: { credits },
        }),
      ),
  });
}

// ============================================================================
// Packet G — player documents and saves. Appended as one block, at the file's
// end, per the wave-3 coordination note: packet H edits this same file for
// rooms and the two diffs must not interleave.
// ============================================================================

/**
 * The domain types for this block, lifted out of the schema the same way the
 * ones above it are lifted out of `components["schemas"]` — except through
 * `ControlComponents`, because `packages/api/src/index.ts` is that lifting
 * done by hand for every other type here, and it is not a file this packet
 * owns. The generated shape already exists on the wire; this only names it.
 */
export type PlayerDocumentCollection = ControlComponents["schemas"]["PlayerDocumentCollection"];
export type PlayerDocument = ControlComponents["schemas"]["PlayerDocument"];
export type PlayerSaveSummary = ControlComponents["schemas"]["PlayerSaveSummary"];
export type PlayerSave = ControlComponents["schemas"]["PlayerSave"];

/**
 * Every collection a player's document store has anything in, third-person
 * and read-only — the support view `listPlayerCollections` describes, not
 * the game's own. There is no environment-wide read on this surface: v1's
 * admin routes read a whole environment by path because their rows had no
 * owner, which is also how its client routes came to read each other's data.
 */
export function usePlayerDocumentCollections(envId: string, playerId: string) {
  const api = useApi();
  return useQuery({
    queryKey: keys.playerDocumentCollections(envId, playerId),
    queryFn: async (): Promise<PlayerDocumentCollection[]> => {
      const res = await api.GET("/v1/environments/{envId}/players/{playerId}/documents", {
        params: { path: { envId, playerId } },
      });
      return unwrap(res).collections;
    },
  });
}

/**
 * One page of one collection.
 *
 * `keepPreviousData` so choosing a different collection, or paging one, does
 * not blank the table between the old page leaving and the new one arriving.
 */
export function usePlayerDocuments(
  envId: string,
  playerId: string,
  collection: string,
  opts: { after?: string } = {},
) {
  const api = useApi();
  const after = opts.after ?? "";
  return useQuery({
    queryKey: keys.playerDocuments(envId, playerId, collection, after),
    enabled: Boolean(collection),
    placeholderData: keepPreviousData,
    queryFn: async () =>
      unwrap(
        await api.GET("/v1/environments/{envId}/players/{playerId}/documents/{collection}", {
          params: {
            path: { envId, playerId, collection },
            query: { after: after || undefined },
          },
        }),
      ),
  });
}

/**
 * The slots a player holds, without their bytes — see `listPlayerSaves`'s own
 * description for why a list of sixteen half-megabyte slots does not carry
 * the bytes: `usePlayerSave` below reads one at a time, on demand.
 */
export function usePlayerSaves(envId: string, playerId: string) {
  const api = useApi();
  return useQuery({
    queryKey: keys.playerSaves(envId, playerId),
    queryFn: async (): Promise<PlayerSaveSummary[]> => {
      const res = await api.GET("/v1/environments/{envId}/players/{playerId}/saves", {
        params: { path: { envId, playerId } },
      });
      return unwrap(res).saves;
    },
  });
}

/**
 * One save slot, bytes included — fetched on demand only.
 *
 * A slot is up to half a megabyte and a player may hold sixteen of them, so
 * loading every slot the moment the Saves table renders would make opening
 * the player screen the most expensive request the console makes. `enabled`
 * defaults to true so a caller that has already decided to fetch gets an
 * ordinary query; a screen that has not decided yet passes `enabled: false`
 * until something — a Download click — decides for it.
 */
export function usePlayerSave(
  envId: string,
  playerId: string,
  slot: string,
  opts: { enabled?: boolean } = {},
) {
  const api = useApi();
  return useQuery({
    queryKey: keys.playerSave(envId, playerId, slot),
    enabled: Boolean(slot) && (opts.enabled ?? true),
    queryFn: async (): Promise<PlayerSave> =>
      unwrap(
        await api.GET("/v1/environments/{envId}/players/{playerId}/saves/{slot}", {
          params: { path: { envId, playerId, slot } },
        }),
      ),
  });
}

// ============================================================================
// Packet H — rooms: decisions/0034's console surface. What modules/roomstate
// and presence together say a room is holding right now, and the ticket
// "Watch live" spends to keep that current from the socket instead of a poll.
// Appended as one block, at the file's end, per the wave-3 coordination note
// beside packet G's own block above.
// ============================================================================

/**
 * The domain types for this block, lifted out of the schema the way packet
 * G's own block does just above — through `ControlComponents`, already
 * imported for exactly that. See `cmd/control/handler/roomstate.go`.
 */
export type RoomSummary = ControlComponents["schemas"]["RoomSummary"];
export type RoomStateEntry = ControlComponents["schemas"]["RoomStateEntry"];
export type RealtimeTicket = ControlComponents["schemas"]["RealtimeTicket"];

/**
 * What this environment has anything to show for right now — a room somebody
 * is in, a room holding a saved board, or both; `listRooms` unions the two.
 * Polled at the same cadence `useLiveStats` and `useLiveLeaderboards` already
 * poll the rest of Operate at: a room's roster and its `live` flag both
 * change with nobody on this screen doing anything.
 */
export function useRooms(envId: string) {
  const api = useApi();
  return useQuery({
    queryKey: keys.rooms(envId),
    queryFn: async (): Promise<RoomSummary[]> =>
      unwrap(
        await api.GET("/v1/environments/{envId}/rooms", {
          params: { path: { envId } },
        }),
      ).rooms,
    refetchInterval: 5000,
  });
}

/**
 * Every key one room currently holds.
 *
 * Polled at the same cadence by default, but `use-room-watch.ts` keeps this
 * exact query's cache current from the socket while "Watch live" is on, and
 * the room screen passes `refetchInterval: false` for as long as that is
 * true — a poll racing a socket update is how a value flickers back to what
 * it was a moment before.
 */
export function useRoomState(
  envId: string,
  room: string,
  options: { enabled?: boolean; refetchInterval?: number | false } = {},
) {
  const api = useApi();
  return useQuery({
    queryKey: keys.roomState(envId, room),
    enabled: (options.enabled ?? true) && Boolean(room),
    refetchInterval: options.refetchInterval ?? 5000,
    queryFn: async (): Promise<RoomStateEntry[]> =>
      unwrap(
        await api.GET("/v1/environments/{envId}/rooms/{room}/state", {
          params: { path: { envId, room } },
        }),
      ).entries,
  });
}

/**
 * The console's own handshake credential for "Watch live" —
 * `mintConsoleRealtimeTicket`, minted for no player. Not cached: a ticket is
 * single-use, so the only sane place for one to live is the socket that is
 * about to spend it, which is `use-room-watch.ts`.
 */
export function useMintWatchTicket(envId: string) {
  const api = useApi();
  return useMutation({
    mutationFn: async (): Promise<RealtimeTicket> =>
      unwrap(
        await api.POST("/v1/environments/{envId}/realtime/ticket", {
          params: { path: { envId } },
        }),
      ),
  });
}

// ===== Phase 1 — credit usage, one row per charge =====

/**
 * Every charge an organisation has been recorded for, newest first — the
 * itemised read behind Plan's totals.
 *
 * A cursor rather than a page number, following `usePlayers`'s own reason: a
 * total that might disagree with the rows under insert load is worse than no
 * total, and the screen already says so by carrying none. Not cached across
 * cursors the way `useMembers` is on `orgId` alone — each page is its own
 * request, kept on screen by `placeholderData` while the next one loads so
 * Older/Newer never flash empty.
 */
export function useUsageEvents(orgId: string, cursor = "") {
  const api = useApi();
  return useQuery({
    queryKey: ["org", orgId, "usage-events", cursor] as const,
    queryFn: async (): Promise<{ events: UsageEvent[]; nextCursor: string | undefined }> => {
      const page = unwrap(
        await api.GET("/v1/orgs/{orgId}/usage/events", {
          params: { path: { orgId }, query: cursor ? { after: cursor } : {} },
        }),
      );
      return { events: page.events, nextCursor: page.next_cursor ?? undefined };
    },
    enabled: Boolean(orgId),
    placeholderData: keepPreviousData,
  });
}
